import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { apiUrl, getErrorMessage, mediaUrl } from '../utils/api';

const readUserInfo = () => {
  try { return JSON.parse(localStorage.getItem('userInfo') || 'null'); }
  catch { return null; }
};

export default function Favorites() {
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [removingId, setRemovingId] = useState('');

  useEffect(() => {
    let cancelled = false;
    const info = readUserInfo();
    if (!info?.token) {
      setError('Please sign in to view your favorites.');
      setLoading(false);
      return () => { cancelled = true; };
    }

    const loadFavorites = async () => {
      try {
        const headers = { Authorization: `Bearer ${info.token}` };
        const { data: saved } = await axios.get(apiUrl('/api/auth/favorites'), { headers });
        const ids = Array.isArray(saved.favoriteBusinessIds) ? saved.favoriteBusinessIds.map(String) : [];
        if (!ids.length) {
          if (!cancelled) setFavorites([]);
          return;
        }
        const { data } = await axios.get(apiUrl('/api/businesses'));
        const order = new Map(ids.map((id, index) => [id, index]));
        const businesses = (Array.isArray(data) ? data : [])
          .filter((business) => order.has(String(business._id)))
          .sort((a, b) => order.get(String(a._id)) - order.get(String(b._id)));
        if (!cancelled) setFavorites(businesses);
      } catch (requestError) {
        if (!cancelled) {
          setError(requestError?.response?.status === 401
            ? 'Your sign-in has expired. Please sign in again to view your favorites.'
            : getErrorMessage(requestError, 'Could not load your favorites.'));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadFavorites();
    return () => { cancelled = true; };
  }, []);

  const removeFavorite = async (businessId) => {
    const info = readUserInfo();
    if (!info?.token) {
      setError('Your sign-in has expired. Please sign in again to manage favorites.');
      return;
    }
    const id = String(businessId);
    setError('');
    setRemovingId(id);
    try {
      await axios.delete(apiUrl(`/api/auth/favorites/${id}`), { headers: { Authorization: `Bearer ${info.token}` } });
      setFavorites((current) => current.filter((business) => String(business._id) !== id));
      setError('');
    } catch (requestError) {
      setError(requestError?.response?.status === 401
        ? 'Your sign-in has expired. Please sign in again to manage favorites.'
        : getErrorMessage(requestError, 'Could not update favorites.'));
    } finally {
      setRemovingId('');
    }
  };

  return (
    <div className="page favorites-page">
      <section className="section-header favorites-heading">
        <div><p className="eyebrow">Saved providers</p><h1>My Favorites</h1><p className="muted-text">Your favorite businesses, all in one place.</p></div>
        {!loading && favorites.length > 0 && <span className="favorites-count">{favorites.length} saved</span>}
      </section>
      {error && <p className="error-message" role="alert">{error}</p>}
      {loading && <div className="panel" role="status">Loading your favorites…</div>}
      {!loading && !error && favorites.length === 0 && <div className="panel favorites-empty"><h2>No favorites saved yet</h2><p className="muted-text">Tap the heart on a business in the marketplace to save it here.</p><Link className="button primary" to="/">Explore businesses</Link></div>}
      {!loading && !error && favorites.length > 0 && (
        <section className="business-grid favorites-grid" aria-label="Your favorite businesses">
          {favorites.map((business) => {
            const id = String(business._id);
            return (
              <article className="business-card favorites-card" key={id}>
                <div className="business-card-topline"><span className="business-type">{business.type || 'Business'}</span><button type="button" className="favorite-button active" onClick={() => removeFavorite(id)} disabled={Boolean(removingId)} aria-label={`Remove ${business.name} from favorites`} title="Remove from favorites">{removingId === id ? '…' : '♥'}</button></div>
                <div className="business-card-image">{business.heroImage ? <img src={mediaUrl(business.heroImage)} alt={`${business.name} hero`} /> : <span>{String(business.name || 'E').slice(0, 1).toUpperCase()}</span>}</div>
                <h2>{business.name}</h2>
                <p>{business.description || 'Explore services and appointment availability.'}</p>
                <div className="business-meta"><span>📍 {[business.city, business.country].filter(Boolean).join(', ') || 'Location not provided'}</span><span>⭐ {business.rating || 0} ({business.reviewCount || 0})</span></div>
                <div className="hero-actions compact"><Link className="button primary" to={`/business/${business.slug}`}>View profile</Link></div>
              </article>
            );
          })}
        </section>
      )}
    </div>
  );
}
